"use client";

import React, { useState } from "react";
import Breadcrumb from "@/components/Common/Breadcrumb";
import { Button } from "@/components/ui/button";
import { getImagePath } from "@/lib/utils";
import { useTranslations } from "next-intl";

const Page = () => {
  const t = useTranslations("producercalculator");

  const [villages, setVillages] = useState<number | "">("");
  const [playsPerVillage, setPlaysPerVillage] = useState<number | "">("");
  const [days, setDays] = useState<number | "">("");
  const [showResult, setShowResult] = useState(false);

  const toInt = (v: number | "") => (typeof v === "number" ? v : 0);

  const totalPlays = toInt(villages) * toInt(playsPerVillage) * toInt(days);

  const earnings = totalPlays * 49;

  const numberToWords = (num: number): string => {
    if (num >= 10000000) {
      const crores = Math.floor(num / 10000000);
      const lakhs = Math.floor((num % 10000000) / 100000);
      return lakhs ? `${crores} crore ${lakhs} lakh` : `${crores} crore`;
    } else if (num >= 100000) {
      const lakhs = Math.floor(num / 100000);
      const thousands = Math.floor((num % 100000) / 1000);
      return thousands
        ? `${lakhs} lakh ${thousands} thousand`
        : `${lakhs} lakh`;
    } else if (num >= 1000) {
      const thousands = Math.floor(num / 1000);
      return `${thousands} thousand`;
    }
    return `${num}`;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!toInt(villages) || !toInt(playsPerVillage) || !toInt(days)) return;
    setShowResult(true);
  };

  const handleReset = () => setShowResult(false);

  return (
    <section className="section-padding bg-white py-24">
      <Breadcrumb
        pageName={t("page_title")}
        description=""
        bgImages={[getImagePath("/images/breadcrumb/howItWorks.webp")]}
        bgIndex={0}
        buttons={[
          {
            label: t("join_now"),
            href: "/join-producer",
            props: {
              variant: "default",
              className:
                "ml-2 rounded-tl-xl rounded-br-xl bg-gradient-to-r from-[#003399] to-[#0099CC] text-white border-white border hover:bg-primary hover:text-white",
            },
          },
        ]}
      />

      <div className="container mx-auto px-4 pt-10">
        <h3 className="text-center text-3xl text-[#003399]">{t("heading")}</h3>

        <form
          className="space-y-4 px-20 py-20 mt-5 max-w-2xl mx-auto bg-proCalulator rounded-tl-[30px] rounded-br-[30px]"
          onSubmit={handleCalculate}
        >
          {!showResult && (
            <>
              <div className="w-full mb-3">
                <label className="block mb-1 text-white">
                  {t("label_villages")}
                </label>
                <input
                  type="number"
                  required
                  min={1}
                  value={villages}
                  onChange={(e) =>
                    setVillages(
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder={t("placeholder_villages")}
                  className="border-0 text-white placeholder:text-gray-300 bg-black/70 p-2 w-full rounded"
                />
              </div>

              <div className="w-full mb-3">
                <label className="block mb-1 text-white">
                  {t("label_plays")}
                </label>
                <input
                  type="number"
                  required
                  min={1}
                  value={playsPerVillage}
                  onChange={(e) =>
                    setPlaysPerVillage(
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder={t("placeholder_plays")}
                  className="border-0 text-white placeholder:text-gray-300 bg-black/70 p-2 w-full rounded"
                />
              </div>

              <div className="w-full mb-3">
                <label className="block mb-1 text-white">
                  {t("label_days")}
                </label>
                <input
                  type="number"
                  required
                  min={1}
                  value={days}
                  onChange={(e) =>
                    setDays(e.target.value === "" ? "" : Number(e.target.value))
                  }
                  placeholder={t("placeholder_days")}
                  className="border-0 text-white placeholder:text-gray-300 bg-black/70 p-2 w-full rounded"
                />
              </div>
            </>
          )}

          {showResult && (
            <div className="text-center text-white py-16 px-10">
              <p className="text-white">{t("result_title")}</p>

              <div className="mt-3 grid gap-2">
                <div>
                  {t("result_totalplays")}: <strong>{totalPlays}</strong>
                </div>

                <div>
                  {t("result_rate")}: <strong>₹ 49</strong>
                </div>

                <div className="mt-2">
                  {t("result_totalincome")}:{" "}
                  <strong className="text-3xl md:text-5xl">₹ {earnings}</strong>
                </div>

                <div className="opacity-90 text-sm">
                  ({numberToWords(earnings)} rupees)
                </div>
              </div>

              <div className="mt-8 flex justify-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  className="border bg-transparent border-white rounded-tl-xl rounded-br-xl text-white px-6 py-2 hover:bg-[#003399]"
                >
                  {t("reset")}
                </Button>

                <Button
                  asChild
                  className="bg-white text-[#003399] rounded-tl-xl rounded-br-xl px-6 py-2 hover:bg-white/90"
                >
                  <a href="/join-producer">{t("join_now")}</a>
                </Button>
              </div>
            </div>
          )}

          {!showResult && (
            <div className="text-center mt-6">
              <Button
                type="submit"
                className="border bg-transparent border-white text-white px-6 py-2 rounded-tl-xl rounded-br-xl hover:bg-[#003399]"
              >
                {t("calculate")}
              </Button>
            </div>
          )}
        </form>

        <p className="text-center text-base text-gray-400 mt-4">
          {t("disclaimer")}
        </p>
      </div>
    </section>
  );
};

export default Page;
